import { useCallback } from 'react';
import { usePostHog } from '@posthog/react';
import { AssignmentEntryPoint, AssignmentType, StudentGroup, StudentSelection } from '../models/Assignment';

export type AssignmentEventName = 'assignment_created';

/** The payload as PostHog receives it: snake_case keys. */
export interface AssignmentEventProperties {
  entry_point: AssignmentEntryPoint;
  assignment_type: AssignmentType;
  student_groups_selected: StudentGroup[];
  student_selection: StudentSelection;
  support_skill_warning_shown: boolean;
  /** Only meaningful when support_skill_warning_shown is true. */
  do_not_include_used: boolean;
  /** The class the assignment was created for. The sidebar allows exactly one. */
  class_id: string;
  /**
   * The target skill, the same one the create mutation is keyed on, carried as its business code
   * (for example `391`) rather than its Relay id. A `skill_pathway` assignment also covers support
   * skills, but those vary per student and are not reported here.
   */
  skill_id: string;
}

export type CaptureAssignmentEvent = (name: AssignmentEventName, properties: AssignmentEventProperties) => void;

/**
 * The single capture point for assignment analytics. Both the event name and the payload are
 * typed so a call site can neither invent an event nor forget a property.
 */
export function useAssignmentEvent(): CaptureAssignmentEvent {
  const postHog = usePostHog();

  return useCallback<CaptureAssignmentEvent>(
    (name, properties) => {
      postHog.capture(name, properties);
    },
    [postHog],
  );
}
